import { Capacitor } from '@capacitor/core';
import api, { getSessionToken } from '@/lib/api';
import BackgroundGeolocation, {
  ActivityType,
  DesiredAccuracy,
  LogLevel,
} from '@transistorsoft/capacitor-background-geolocation';

const API_ROOT = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'ca_native_tracking_session';
let ready;
let starting = false;
let trackingEpoch = 0;
let listenersInstalled = false;
let boundedSession;
export const isNativeTrackingStarting = () => starting;

export function nativeTrackingSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY)) || null; } catch { return null; }
}

export function supportsNativeBackgroundLocation() {
  return Capacitor.isNativePlatform();
}

function authorizationHeaders() {
  const token = getSessionToken();
  return token ? { Authorization: `Bearer ${token}`, 'X-CA-Native-App': '1' } : {};
}

function installTrackingListeners() {
  if (listenersInstalled) return;
  BackgroundGeolocation.onHttp(({ status }) => {
    if ([401, 403, 409].includes(status)) void stopNativeDriverTracking().catch(() => {});
  });
  window.addEventListener('ca-credential-changed', () => {
    void stopNativeDriverTracking().catch(() => {});
  });
  listenersInstalled = true;
}

function sessionLifetimeMinutes(session, serviceId) {
  const expires = typeof session?.expires_at === 'string' ? Date.parse(session.expires_at) : NaN;
  // Zero disables the native timer. Never start a session with less than one
  // full minute remaining, and do not extend the backend's 12-hour ceiling.
  const minutes = Math.min(720, Math.floor((expires - Date.now() - 1000) / 60000));
  if (!session || typeof session.session_id !== 'string' || !session.session_id
      || session.session_id.length > 128 || typeof serviceId !== 'string' || !serviceId
      || session.service_id !== serviceId
      || !Number.isFinite(minutes) || minutes < 1) {
    throw new Error('No pudimos confirmar una sesión de seguimiento vigente.');
  }
  return minutes;
}

/**
 * Prepare the native location engine for an authenticated driver.
 *
 * This is intentionally not started automatically. The driver must first
 * activate availability and accept the operating-system permission prompts.
 */
export async function configureNativeDriverTracking() {
  if (!supportsNativeBackgroundLocation()) return { enabled: false, web: true };
  if (!API_ROOT) throw new Error('REACT_APP_BACKEND_URL no está configurado.');

  if (ready) return ready;
  installTrackingListeners();
  ready = BackgroundGeolocation.ready({
    // Reopening the app must preserve a running service's HTTP session.
    reset: false,
    logger: {
      debug: false,
      logLevel: LogLevel.Off,
      logMaxDays: 1,
    },
    geolocation: {
      desiredAccuracy: DesiredAccuracy.High,
      activityType: ActivityType.AutomotiveNavigation,
      distanceFilter: 25,
      stationaryRadius: 35,
      stopTimeout: 5,
      locationAuthorizationRequest: 'Always',
      showsBackgroundLocationIndicator: true,
    },
    activity: {
      disableMotionActivityUpdates: false,
    },
    app: {
      stopOnTerminate: false,
      startOnBoot: false,
      enableHeadless: true,
      notification: {
        title: 'C&A · Seguimiento activo',
        text: 'Tu ubicación se comparte durante la operación.',
        channelName: 'Seguimiento de servicios',
        sticky: true,
        color: '#E31B2B',
      },
      backgroundPermissionRationale: {
        title: 'Permitir ubicación en segundo plano',
        message: 'C&A necesita tu ubicación durante un servicio activo para calcular la llegada y mostrar el avance de la grúa al cliente.',
        positiveAction: 'Abrir configuración',
        negativeAction: 'Ahora no',
      },
    },
    persistence: {
      maxDaysToPersist: 1,
      maxRecordsToPersist: 1500,
      locationTemplate: '{"lat":<%= latitude %>,"lng":<%= longitude %>,"accuracy":<%= accuracy %>,"uuid":"<%= uuid %>","timestamp":"<%= timestamp %>"}',
    },
    http: {
      url: `${API_ROOT}/api/driver/native-location`,
      rootProperty: '.',
      headers: authorizationHeaders(),
      autoSync: true,
      autoSyncThreshold: 0,
      batchSync: false,
      timeout: 15000,
    },
  }).then(async (state) => {
    // reset:false ignores ready() config after the first installation. Migrate
    // these privacy settings explicitly while preserving an active trip's HTTP.
    await BackgroundGeolocation.setConfig({
      app: { startOnBoot: false },
      logger: { debug: false, logLevel: LogLevel.Off, logMaxDays: 1 },
      persistence: {
        locationTemplate: '{"lat":<%= latitude %>,"lng":<%= longitude %>,"accuracy":<%= accuracy %>,"uuid":"<%= uuid %>","timestamp":"<%= timestamp %>"}',
      },
    });
    return state;
  }).catch((error) => { ready = undefined; throw error; });
  return ready;
}

export async function startNativeDriverTracking(serviceId) {
  if (!supportsNativeBackgroundLocation()) return false;
  if (!serviceId || starting) throw new Error('Espera y selecciona un servicio activo.');
  starting = true;
  const ticket = ++trackingEpoch;
  const ownerToken = getSessionToken();
  const current = () => ticket === trackingEpoch && !!ownerToken && getSessionToken() === ownerToken;
  const assertCurrent = () => { if (!current()) throw new Error('La sesión cambió. Vuelve a entrar.'); };
  let receivedSession;
  try {
    await haltNativeDriverTracking();
    assertCurrent();
    const { data: session } = await api.post('/driver/native-tracking/start', { service_id: serviceId });
    receivedSession = session;
    assertCurrent();
    const lifetime = sessionLifetimeMinutes(session, serviceId);
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    await BackgroundGeolocation.setConfig({
      geolocation: { stopAfterElapsedMinutes: lifetime },
      persistence: {
        locationTemplate: '{"lat":<%= latitude %>,"lng":<%= longitude %>,"accuracy":<%= accuracy %>,"uuid":"<%= uuid %>","timestamp":"<%= timestamp %>"}',
      },
      http: { url: `${API_ROOT}/api/driver/native-location`, rootProperty: '.',
        headers: authorizationHeaders(), params: { session_id: session.session_id } },
    });
    assertCurrent();
    sessionLifetimeMinutes(session, serviceId);
    if (Date.now() + lifetime * 60000 >= Date.parse(session.expires_at)) {
      throw new Error('La sesión de seguimiento dejó de estar vigente. Intenta nuevamente.');
    }
    await BackgroundGeolocation.start();
    assertCurrent();
    sessionLifetimeMinutes(session, serviceId);
    if (Date.now() + lifetime * 60000 >= Date.parse(session.expires_at)) {
      throw new Error('La sesión de seguimiento dejó de estar vigente. Intenta nuevamente.');
    }
    boundedSession = `${session.session_id}:${session.expires_at}`;
    return true;
  } catch (error) {
    // Stop locally even when a prior stop preceded the backend response. The
    // extra late-response cleanup below uses only the still-current owner.
    let closeError;
    await stopNativeDriverTracking().catch(failure => { closeError = failure; });
    if (receivedSession?.session_id && getSessionToken() === ownerToken) {
      await api.post('/driver/native-tracking/stop', { session_id: receivedSession.session_id }, { timeout: 5000 }).catch(() => {});
    }
    throw closeError || error;
  } finally { starting = false; }
}

async function haltNativeDriverTracking() {
  if (!supportsNativeBackgroundLocation()) return false;
  boundedSession = undefined;
  const session = nativeTrackingSession();
  let failed = false;
  try { localStorage.removeItem(SESSION_KEY); } catch { failed = true; }
  try { await configureNativeDriverTracking(); } catch { failed = true; }
  // Stop collection before purging: a last fix can otherwise enter the queue
  // after a concurrent purge. Still attempt the purge when native stop fails.
  try { await BackgroundGeolocation.stop(); } catch { failed = true; }
  try { await BackgroundGeolocation.destroyLocations(); } catch { failed = true; }
  if (session) {
    // Local stop works offline; the backend also checks assignment on every upload.
    await api.post('/driver/native-tracking/stop', { session_id: session.session_id }, { timeout: 5000 }).catch(() => {});
  }
  if (failed) {
    throw new Error('No se pudo confirmar el cierre del GPS.');
  }
  return true;
}

export async function stopNativeDriverTracking() {
  ++trackingEpoch;
  return haltNativeDriverTracking();
}

export async function isNativeDriverTrackingActive() {
  if (!supportsNativeBackgroundLocation()) return false;
  await configureNativeDriverTracking();
  const state = await BackgroundGeolocation.getState();
  // Migrate old, unscoped SDK configurations safely on the first launch.
  const session = nativeTrackingSession();
  if (state.enabled || session) {
    try {
      const lifetime = sessionLifetimeMinutes(session, session?.service_id);
      if (!getSessionToken()) throw new Error('Sesión cerrada');
      const signature = `${session.session_id}:${session.expires_at}`;
      if (state.enabled && boundedSession !== signature) {
        // A pre-upgrade reset:false install may have persisted no native timer.
        // Rebind only its expiry, never its authorization or upload endpoint.
        const ticket = trackingEpoch, token = getSessionToken();
        await BackgroundGeolocation.setConfig({ geolocation: { stopAfterElapsedMinutes: lifetime } });
        if (ticket !== trackingEpoch || token !== getSessionToken()
            || Date.now() + lifetime * 60000 >= Date.parse(session.expires_at)) {
          throw new Error('La sesión cambió');
        }
        boundedSession = signature;
      }
    } catch {
      await stopNativeDriverTracking();
      return false;
    }
  }
  return Boolean(state.enabled);
}
